import React, { useEffect, useState } from "react";

import { Prompt, useHistory } from "react-router-dom";
import { Location, LocationDescriptor } from "history";

import { useTranslation } from "react-i18next";

import ConfirmationDialog from "../ConfirmationDialog";

const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
  if (window.KEYCLOAK?.authenticated) {
    e.preventDefault();

    // for older browsers:
    if (e) {
      e.returnValue = "";
    }
  } else {
    delete e.returnValue;
  }
};

interface NavigationPromptProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  location: string | LocationDescriptor | (() => void) | null;
  setLocation: (pathname: string) => void;
}

const NavigationPrompt = ({
  open = false,
  setOpen,
  location,
  setLocation,
}: NavigationPromptProps): JSX.Element => {
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const allowOrBlockNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation) {
      setLocation(nextLocation.pathname);
      setOpen(true);
      return false;
    }

    return true;
  };

  const confirmNavigation = () => {
    setOpen(false);
    setConfirmedNavigation(true);
  };

  const history = useHistory();

  useEffect(() => {
    if (confirmedNavigation) {
      if (typeof location === "function") {
        // call the external function that will navigate
        location?.();
      } else if (location != null) {
        // navigate to the blocked location
        history.push(location);
      }
    }
  }, [confirmedNavigation, history, location]);

  useEffect(() => {
    window?.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      window?.removeEventListener("beforeunload", beforeUnloadHandler);
    };
  }, []);

  const { t } = useTranslation("common");

  return (
    <>
      <Prompt when message={allowOrBlockNavigation} />

      <ConfirmationDialog
        isOpen={open}
        onConfirm={confirmNavigation}
        onCancel={() => setOpen(false)}
        title={t("navigationPrompt.title")}
        message={t("navigationPrompt.body")}
      />
    </>
  );
};

export default NavigationPrompt;
